/**
 * SoleadLab 2.0 — Ghost-candidate accept core (fault-detection review)
 *
 * Shared by DetectionGhostPopup (per-ghost Accept) and DetectionPanel
 * (bulk Accept), so the accept flows can never drift.
 *
 * REWRITTEN 2026-09-02 (derive routing): the indication is built WITH the
 * ghost ring as its geometry, and topology derives from that real ring —
 * the old flow created a default rect at the centroid, derived there, then
 * swapped the geometry without re-deriving, so the fields described a
 * location the mark no longer occupied. Building with the final geometry
 * also kills the un-versioned-PATCH retry dance the old geometry override
 * needed (the CREATE now carries the right ring from the start).
 *
 * NOT here: markAccepted / notifications — callers own detection-store
 * bookkeeping and UX, mirroring utils/createIndication.ts's split.
 */

import { useProjectStore } from '../stores/projectStore';
import type { IndicationType, Indication } from '../stores/projectStore';
import type { DetectionCandidate } from '../stores/detectionStore';
import {
  buildManualIndication,
  quickIndicationForm,
  deriveTopologyIntoIndication,
} from './createIndication';
import { centroidOfPolygon } from './geometry';

/**
 * BUILD (no store write) an accepted-ghost indication: creation-core
 * defaults at the ring centroid, geometry = the ghost ring, topology derived
 * from that ring. Returns null when creation is refused (share-viewer
 * defense inside the core) or the ring has no centroid.
 */
export function buildAcceptedIndication(
  ring: [number, number][],
  type: IndicationType,
  opts?: { rotationDeg?: number; orthoLayerId?: string },
): Indication | null {
  const center = centroidOfPolygon(ring);
  if (!center) return null;
  const base = buildManualIndication(center, quickIndicationForm(type));
  if (!base) return null;
  const withGeometry: Indication = {
    ...base,
    geometry: { type: 'Polygon', coordinates: [ring] },
    properties: {
      ...base.properties,
      ...(opts?.orthoLayerId ? { orthoLayerId: opts.orthoLayerId } : {}),
      ...(opts?.rotationDeg != null ? { rotationDeg: opts.rotationDeg } : {}),
    },
  };
  return deriveTopologyIntoIndication(withGeometry);
}

/**
 * Accept one ghost candidate as a NEW indication of `type`. Returns the new
 * indication id, or null when creation is refused. Caller does markAccepted
 * + notification.
 */
export function acceptGhostAsIndication(
  cand: DetectionCandidate,
  type: IndicationType,
): string | null {
  const indication = buildAcceptedIndication(cand.ring, type, {
    rotationDeg: cand.props.rotationDeg,
  });
  if (!indication) return null;
  const st = useProjectStore.getState();
  st.addIndication(indication);
  st.updateAvailableDates();
  return indication.id;
}
